import { Check, ChevronDown, ChevronUp, Search } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { Input } from "../../../../components/ui/input"
import { ActiveFilterIcon } from "@/global_filter"
import { useState } from "preact/hooks"
import { getPromotionTypeColors, getFilterLabelColors } from '../../promo_creation/constants'
import {
  FilterCategoryKey,
  PromoCalendarFilterPreferencesState,
  setPromoCalendarCategoryCollapsed,
  setPromoCalendarFiltersVisible
} from "../filter_preferences"

// Product grouping types and colors
type ProductGroup = 'all_caps' | 'starts_with_number' | 'other'

interface ProductGroupColors {
  background: string
  text: string
  selectedBackground: string
  selectedText: string
  border: string
}

const PRODUCT_GROUP_COLORS: Record<ProductGroup, ProductGroupColors> = {
  all_caps: {
    background: '#fef3c7', // yellow-100
    text: '#92400e', // yellow-800
    selectedBackground: '#f59e0b', // yellow-500
    selectedText: '#ffffff',
    border: '#f59e0b' // yellow-500
  },
  starts_with_number: {
    background: '#dbeafe', // blue-100
    text: '#1e40af', // blue-800
    selectedBackground: '#3b82f6', // blue-500
    selectedText: '#ffffff',
    border: '#3b82f6' // blue-500
  },
  other: {
    background: '#f3e8ff', // purple-100
    text: '#6b21a8', // purple-800
    selectedBackground: '#8b5cf6', // purple-500
    selectedText: '#ffffff',
    border: '#8b5cf6' // purple-500
  }
}

function categorizeProduct(product: string): ProductGroup {
  // Check if product starts with a number first (this takes priority)
  if (/^\d/.test(product)) {
    return 'starts_with_number'
  }
  
  // Check if product is all caps and contains only letters (no numbers)
  const hasLetters = /[a-zA-Z]/.test(product)
  const hasNumbers = /\d/.test(product)
  const isAllCaps = hasLetters && product === product.toUpperCase()
  
  if (isAllCaps && !hasNumbers) {
    return 'all_caps'
  }
  
  // Everything else
  return 'other'
}

interface FilterOptions {
  customers: string[]
  retailers: string[]
  promotionTypes: string[]
  products: string[]
}

interface CalendarFiltersProps {
  filterOptions: FilterOptions
  selectedCustomers: string[]
  selectedRetailers: string[]
  selectedPromotionTypes: string[]
  selectedProducts: string[]
  searchTerm: string
  onCustomerChange: (customers: string[]) => void
  onRetailerChange: (retailers: string[]) => void
  onPromotionTypeChange: (types: string[]) => void
  onProductChange: (products: string[]) => void
  onSearchChange: (term: string) => void
}

export function CalendarFilters({
  filterOptions,
  selectedCustomers,
  selectedRetailers,
  selectedPromotionTypes,
  selectedProducts,
  searchTerm,
  onCustomerChange,
  onRetailerChange,
  onPromotionTypeChange,
  onProductChange,
  onSearchChange,
}: CalendarFiltersProps) {
  const [customerSearchTerm, setCustomerSearchTerm] = useState("")
  const [retailerSearchTerm, setRetailerSearchTerm] = useState("")
  const [promotionTypeSearchTerm, setPromotionTypeSearchTerm] = useState("")
  const [productSearchTerm, setProductSearchTerm] = useState("")
  const preferences = PromoCalendarFilterPreferencesState.use()

  function toggleCategoryCollapsed(category: FilterCategoryKey) {
    const isCollapsed = preferences.collapseState[category]
    setPromoCalendarCategoryCollapsed(category, !isCollapsed)
  }

  function getFilteredCategoryOptions(options: string[], categorySearchTerm: string) {
    if (!categorySearchTerm.trim()) return options
    const searchLower = categorySearchTerm.trim().toLowerCase()
    return options.filter(option => option.toLowerCase().includes(searchLower))
  }

  const filteredCustomers = getFilteredCategoryOptions(filterOptions.customers, customerSearchTerm)
  const filteredRetailers = getFilteredCategoryOptions(filterOptions.retailers, retailerSearchTerm)
  const filteredPromotionTypes = getFilteredCategoryOptions(filterOptions.promotionTypes, promotionTypeSearchTerm)
  const filteredProducts = getFilteredCategoryOptions(filterOptions.products, productSearchTerm)

  function handleCustomerToggle(customer: string) {
    if (selectedCustomers.includes(customer)) {
      onCustomerChange(selectedCustomers.filter(c => c !== customer))
    } else {
      onCustomerChange([...selectedCustomers, customer])
    }
  }

  function handleRetailerToggle(retailer: string) {
    if (selectedRetailers.includes(retailer)) {
      onRetailerChange(selectedRetailers.filter(r => r !== retailer))
    } else {
      onRetailerChange([...selectedRetailers, retailer])
    }
  }

  function handlePromotionTypeToggle(type: string) {
    if (selectedPromotionTypes.includes(type)) {
      onPromotionTypeChange(selectedPromotionTypes.filter(t => t !== type))
    } else {
      onPromotionTypeChange([...selectedPromotionTypes, type])
    }
  }

  function handleProductToggle(product: string) {
    if (selectedProducts.includes(product)) {
      onProductChange(selectedProducts.filter(p => p !== product))
    } else {
      onProductChange([...selectedProducts, product])
    }
  }

  function clearAllFilters() {
    onCustomerChange([])
    onRetailerChange([])
    onPromotionTypeChange([])
    onProductChange([])
  }

  const activeFilterCount =
    selectedCustomers.length + selectedRetailers.length + selectedPromotionTypes.length + selectedProducts.length
  const hasActiveFilters = activeFilterCount > 0

  return (
    <div className="space-y-4">
      {/* Filter Headers */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setPromoCalendarFiltersVisible(!preferences.isFiltersVisible)}
            className="text-xs flex items-center gap-2"
          >
            {preferences.isFiltersVisible ? "Hide Filters" : "Show Filters"}
            {/* Green filter icon so active (possibly hidden) filters are never invisible */}
            {hasActiveFilters && <ActiveFilterIcon />}
            {preferences.isFiltersVisible ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </Button>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search promotions, IDs, products..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.currentTarget.value)}
              className="pl-10 text-sm w-72"
            />
          </div>

          {hasActiveFilters && (
            <span className="text-xs text-gray-500">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active
            </span>
          )}
        </div>

        {hasActiveFilters && (
          <Button
            variant="outline"
            onClick={clearAllFilters}
            className="text-xs"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Filter Content - Collapsible */}
      {preferences.isFiltersVisible && (
        <>
          {/* Customer Filters */}
          {filterOptions.customers.length > 0 && (
            <div>
              <button
                onClick={() => toggleCategoryCollapsed("customers")}
                className="flex items-center gap-2 mb-2"
              >
                {preferences.collapseState.customers ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                )}
                <h5 className="text-xs font-medium text-gray-600">Distributors</h5>
              </button>
              {!preferences.collapseState.customers && (
                <>
                  <Input
                    placeholder="Search distributors..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.currentTarget.value)}
                    className="h-8 text-xs mb-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    {filteredCustomers.map(customer => (
                      <FilterTag
                        key={customer}
                        label={customer}
                        isSelected={selectedCustomers.includes(customer)}
                        onClick={() => handleCustomerToggle(customer)}
                      />
                    ))}
                    {filteredCustomers.length === 0 && (
                      <p className="text-xs text-gray-500">No distributors match your search.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Retailer Filters */}
          {filterOptions.retailers.length > 0 && (
            <div>
              <button
                onClick={() => toggleCategoryCollapsed("retailers")}
                className="flex items-center gap-2 mb-2"
              >
                {preferences.collapseState.retailers ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                )}
                <h5 className="text-xs font-medium text-gray-600">Retailers</h5>
              </button>
              {!preferences.collapseState.retailers && (
                <>
                  <Input
                    placeholder="Search retailers..."
                    value={retailerSearchTerm}
                    onChange={(e) => setRetailerSearchTerm(e.currentTarget.value)}
                    className="h-8 text-xs mb-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    {filteredRetailers.map(retailer => (
                      <FilterTag
                        key={retailer}
                        label={retailer}
                        isSelected={selectedRetailers.includes(retailer)}
                        onClick={() => handleRetailerToggle(retailer)}
                      />
                    ))}
                    {filteredRetailers.length === 0 && (
                      <p className="text-xs text-gray-500">No retailers match your search.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Promotion Type Filters */}
          {filterOptions.promotionTypes.length > 0 && (
            <div>
              <button
                onClick={() => toggleCategoryCollapsed("promotionTypes")}
                className="flex items-center gap-2 mb-2"
              >
                {preferences.collapseState.promotionTypes ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                )}
                <h5 className="text-xs font-medium text-gray-600">Promotion Types</h5>
              </button>
              {!preferences.collapseState.promotionTypes && (
                <>
                  <Input
                    placeholder="Search promotion types..."
                    value={promotionTypeSearchTerm}
                    onChange={(e) => setPromotionTypeSearchTerm(e.currentTarget.value)}
                    className="h-8 text-xs mb-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    {filteredPromotionTypes.map(type => (
                      <PromotionTypeFilterTag
                        key={type}
                        label={type}
                        isSelected={selectedPromotionTypes.includes(type)}
                        onClick={() => handlePromotionTypeToggle(type)}
                      />
                    ))}
                    {filteredPromotionTypes.length === 0 && (
                      <p className="text-xs text-gray-500">No promotion types match your search.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Product Filters */}
          {filterOptions.products.length > 0 && (
            <div>
              <button
                onClick={() => toggleCategoryCollapsed("products")}
                className="flex items-center gap-2 mb-2"
              >
                {preferences.collapseState.products ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                )}
                <h5 className="text-xs font-medium text-gray-600">Products</h5>
              </button>
              {!preferences.collapseState.products && (
                <>
                  <Input
                    placeholder="Search products..."
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.currentTarget.value)}
                    className="h-8 text-xs mb-2"
                  />
                  <div className="flex flex-wrap gap-2">
                    {filteredProducts.map(product => (
                      <ProductFilterTag
                        key={product}
                        label={product}
                        isSelected={selectedProducts.includes(product)}
                        onClick={() => handleProductToggle(product)}
                      />
                    ))}
                    {filteredProducts.length === 0 && (
                      <p className="text-xs text-gray-500">No products match your search.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface FilterTagProps {
  label: string
  isSelected: boolean
  onClick: () => void
}

function FilterTag({ label, isSelected, onClick }: FilterTagProps) {
  const colors = getFilterLabelColors(label)
  
  return (
    <button
      onClick={onClick}
      style={{
        backgroundColor: isSelected ? colors.selectedBackground : colors.background,
        color: isSelected ? colors.selectedText : colors.text,
        border: isSelected ? `2px solid ${colors.border}` : 'none'
      }}
      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105"
    >
      <div className="flex items-center gap-1.5">
        {isSelected && <Check className="h-3 w-3" />}
        {label}
      </div>
    </button>
  )
}

function PromotionTypeFilterTag({ label, isSelected, onClick }: FilterTagProps) {
  const colors = getPromotionTypeColors(label)
  
  return (
    <button
      onClick={onClick}
      style={{
        backgroundColor: isSelected ? colors.selectedBackground : colors.background,
        color: isSelected ? colors.selectedText : colors.text,
        border: isSelected ? `2px solid ${colors.border}` : 'none'
      }}
      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105"
    >
      <div className="flex items-center gap-1.5">
        {isSelected && <Check className="h-3 w-3" />}
        {label}
      </div>
    </button>
  )
}

function ProductFilterTag({ label, isSelected, onClick }: FilterTagProps) {
  const colors = getFilterLabelColors(label)
  
  return (
    <button
      onClick={onClick}
      style={{
        backgroundColor: isSelected ? colors.selectedBackground : colors.background,
        color: isSelected ? colors.selectedText : colors.text,
        border: isSelected ? `2px solid ${colors.border}` : 'none'
      }}
      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105"
    >
      <div className="flex items-center gap-1.5">
        {isSelected && <Check className="h-3 w-3" />}
        {label}
      </div>
    </button>
  )
}
